import { useEffect, useState } from "react";
import axios from "axios";
import OpenOpnieuw from "./OpenOpnieuw";
import { Button } from "./ui/button";
import { Loader2, Check, X, ExternalLink, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/** Een voorstel uit de postbus, op glas. Hermes F onderdeel 3.
 *
 *  Dezelfde opmaak als de wijzigkaart van 10c (taak, werkruimte, huidige en
 *  nieuwe waarde naast elkaar), met een andere weg erachter: de WF-40-rij.
 *
 *  BIJ HET TONEN HAALT DE KAART EEN VERS TOKEN OP. Alleen met dat token kan
 *  Bevestig iets doen, en een kaart die elders opnieuw wordt geopend, laat dit
 *  token vervallen. De bevestiging hoort dus bij wat hier op glas staat.
 *
 *  DE VELDEN KOMEN VAN DE SERVER. Bevestig stuurt precies terug wat `toon`
 *  gaf; de server vergelijkt dat letterlijk met de rij.
 */
export const VELDEN = ["taak_id", "titel", "werkruimte_id", "nieuwe_status", "nieuwe_deadline_iso"];

function foutTekst(err, t = (x) => x) {
  return err?.response?.data?.detail || t("Er ging iets mis; er is niets besloten.");
}

export default function PostbusVerzoek({ verzoek, onBesloten }) {
  const { t } = useTaal();
  const [rij, setRij] = useState(verzoek);
  const [token, setToken] = useState("");
  // FIX 4: na een weigering geen stil nieuw token; zie lib/kaartToken.js.
  const [vervallen, setVervallen] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");

  // NA HET OPNIEUW OPHALEN VAN DE LIJST: een besluit van de server wint van wat
  // deze kaart nog vasthield (bijvoorbeeld bevestigd en niet doorgevoerd).
  useEffect(() => {
    if (verzoek.besloten) setRij(verzoek);
  }, [verzoek]);

  const toon = async (nogNodig = () => true) => {
    try {
      const r = await axios.post(`${API}/postbus/verzoek/${verzoek.id}/toon`);
      if (!nogNodig()) return;
      setRij(r.data.verzoek);
      setToken(r.data.token);
      setVervallen(false);
      setFout("");
    } catch (err) {
      if (nogNodig()) setFout(foutTekst(err, t));
    }
  };

  useEffect(() => {
    if (verzoek.besloten) return undefined;
    let weg = false;
    toon(() => !weg);
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verzoek.id, verzoek.besloten]);

  const beslis = async (besluit) => {
    setBezig(true);
    setFout("");
    try {
      const aangeboden = Object.fromEntries(VELDEN.map((v) => [v, rij[v] || ""]));
      const r = await axios.post(`${API}/postbus/verzoek/${rij.id}/besluit`,
        { token, besluit, aangeboden });
      setRij(r.data);
      onBesloten?.(r.data);
    } catch (err) {
      setFout(foutTekst(err, t));
      setToken("");
      setVervallen(true);
      // EEN BEVESTIGING DIE TASQ NIET DOORVOERDE, IS WEL BESLOTEN: de server
      // heeft de reden in de rij gezet. Opnieuw ophalen laat haar zien.
      onBesloten?.(null);
    } finally {
      setBezig(false);
    }
  };

  const regels = (
    <dl className="text-xs space-y-1">
      <div className="flex gap-2">
        <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Taak")}</dt>
        <dd className="min-w-0 font-medium" data-testid="postbus-verzoek-titel">{rij.titel}</dd>
      </div>
      <div className="flex gap-2">
        <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Werkruimte")}</dt>
        <dd className="min-w-0">{rij.werkruimte_naam}</dd>
      </div>
      {rij.nieuwe_status && (
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Status")}</dt>
          <dd className="min-w-0" data-testid="postbus-verzoek-status">
            {rij.huidige_status_op_glas}
            <ArrowRight className="h-3 w-3 mx-1 inline" />
            <span className="font-medium">{rij.nieuwe_status_op_glas}</span>
          </dd>
        </div>
      )}
      {rij.nieuwe_deadline_iso && (
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 text-[hsl(var(--muted-foreground))]">{t("Deadline")}</dt>
          <dd className="min-w-0" data-testid="postbus-verzoek-deadline">
            {rij.huidige_deadline_op_glas}
            <ArrowRight className="h-3 w-3 mx-1 inline" />
            <span className="font-medium">{rij.nieuwe_deadline_op_glas}</span>
          </dd>
        </div>
      )}
    </dl>
  );

  if (rij.besloten) {
    const uitslag = rij.uitgevoerd ? t("Doorgevoerd in TASQ.")
      : rij.besluit === "geweigerd" ? t("Geweigerd. Er is niets gewijzigd.")
      : `Bevestigd, maar niet doorgevoerd: ${rij.fout || t("reden onbekend")}`;
    return (
      <div className="border rounded-lg p-3 space-y-2" data-testid="postbus-verzoek-besloten">
        {regels}
        <p className={`text-xs ${rij.uitgevoerd || rij.besluit === "geweigerd" ? "text-[hsl(var(--muted-foreground))]" : "text-[hsl(var(--destructive))]"}`}
           data-testid="postbus-verzoek-uitslag">{uitslag}</p>
        {rij.uitgevoerd && rij.verwijzing && (
          <a href={rij.verwijzing} target="_blank" rel="noreferrer"
             className="text-xs inline-flex items-center gap-1 underline">
            {t("Open in TASQ")} <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 space-y-3" data-testid="postbus-verzoek">
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        {t("Voorstel uit de {herkomst}. Er gebeurt niets tot je bevestigt.", { herkomst: rij.herkomst || t("postbus") })}
      </p>
      {regels}
      {fout && (
        <p className="text-xs text-[hsl(var(--destructive))]" data-testid="postbus-verzoek-fout">{fout}</p>
      )}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" className="min-h-11 px-4" onClick={() => beslis("bevestigd")}
                disabled={bezig || !token} data-testid="postbus-verzoek-bevestig">
          {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
          {t("Bevestig")}
        </Button>
        <Button size="sm" variant="outline" className="min-h-11 px-4" onClick={() => beslis("geweigerd")}
                disabled={bezig || !token} data-testid="postbus-verzoek-weiger">
          <X className="h-3 w-3 mr-1" />
          {t("Weiger")}
        </Button>
        {vervallen && <OpenOpnieuw onClick={() => toon()} bezig={bezig} />}
      </div>
    </div>
  );
}
